import type { DealerStartedEvent } from '../../models/entities/dealer-api.model';
import type {
  CompletePatrolSessionRequest,
  CompletePatrolSessionResponse,
  PatrolSessionType,
  StartPatrolSessionResponse,
} from '../../models/entities/patrol-session.model';

const SESSION_PREFIX: Record<PatrolSessionType, string> = {
  'manual-patrol': 'dealer-patrol-',
  'site-check': 'dealer-site-check-',
};

/**
 * Dealer has no patrol *session* resource — starting a patrol just creates an event.
 * The session id is therefore a UI-side handle over the event id, and is the only
 * value here the backend does not supply.
 */
export function mapDealerStartedPatrolEvent(
  event: DealerStartedEvent | null | undefined,
  type: PatrolSessionType,
  siteName?: string | null,
): StartPatrolSessionResponse {
  if (!event?.eventId) {
    throw new Error(`Dealer did not return an event for ${type}.`);
  }

  return {
    sessionId: `${SESSION_PREFIX[type]}${event.eventId}`,
    eventId: event.eventId,
    location: siteName?.trim() || `Site ${event.siteId}`,
    type,
    startedAt:
      event.claimedDateTime ??
      event.createdDateTime ??
      event.firstAlarmDateTime ??
      new Date().toISOString(),
  };
}

/** Recovers the Dealer event id from a session id minted by {@link mapDealerStartedPatrolEvent}. */
export function resolvePatrolSessionEventId(sessionId: string): string {
  for (const prefix of Object.values(SESSION_PREFIX)) {
    if (sessionId.startsWith(prefix)) {
      return sessionId.slice(prefix.length);
    }
  }
  return sessionId;
}

/**
 * Builds the completion result from the caller's own decision. Dealer's close
 * response carries only `{ eventId, completedCount }`, so the row is rebuilt from
 * what the agent submitted rather than echoed back by the server.
 */
export function mapCompletePatrolSessionResponse(
  sessionId: string,
  eventId: string,
  body: CompletePatrolSessionRequest,
): CompletePatrolSessionResponse {
  return {
    success: true,
    sessionId,
    patrol: {
      id: eventId,
      patrolStarted: '',
      location: '',
      patrolNote: '',
      agent: '',
      status: body.decision === 'raised' ? 'raised' : 'closed',
      decisionOutcome: body.outcomeLabel,
      completedAt: undefined,
    },
  };
}
